import React, { useState, useEffect } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import { Users } from 'lucide-react';

interface AthleteSelectorProps {
  selectedAthleteId: string;
  setSelectedAthleteId: (id: string) => void;
}

interface LinkedAthlete {
  id: string;
  name: string;
  sport: string;
}

export const TalentSelector: React.FC<AthleteSelectorProps> = ({
  selectedAthleteId,
  setSelectedAthleteId,
}) => {
  const [athletes, setAthletes] = useState<LinkedAthlete[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLinkedAthletes = async () => {
      try {
        // Get current user's agent profile
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        const { data: profile } = await supabase
          .from('profiles')
          .select('id')
          .eq('user_id', user.id)
          .single();

        if (!profile) return;

        const { data: agent } = await supabase
          .from('agents')
          .select('id')
          .eq('profile_id', profile.id)
          .single();

        if (!agent) return;

        // Fetch linked talents
        const { data: linkedAthletes, error } = await supabase
          .from('talents')
          .select('id, name, sport')
          .eq('agent_id', agent.id);

        if (error) throw error;

        setAthletes(linkedAthletes || []);
        
        // Set default selection
        if (!selectedAthleteId && linkedAthletes && linkedAthletes.length > 0) {
          setSelectedAthleteId('my-holdings');
        }
      } catch (error) {
        console.error('Error fetching linked athletes:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchLinkedAthletes();
  }, [selectedAthleteId, setSelectedAthleteId]);

  if (loading) {
    return (
      <div className="w-48 h-10 bg-muted animate-pulse rounded-md"></div>
    );
  }

  return (
    <Select value={selectedAthleteId} onValueChange={setSelectedAthleteId}>
      <SelectTrigger className="w-48">
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4" />
          <SelectValue placeholder="Select view" />
        </div>
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="my-holdings">My Holdings</SelectItem>
        {athletes.length > 0 && (
          <>
            <SelectItem value="all">All Athletes</SelectItem>
            {athletes.map((athlete) => (
              <SelectItem key={athlete.id} value={athlete.id}>
                {athlete.name} ({athlete.sport})
              </SelectItem>
            ))}
          </>
        )}
      </SelectContent>
    </Select>
  );
};